import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TextInput from '@components/dataEntry/Input/TextInput';
import Button from '@components/dataEntry/button/Button';
import RadioBox from '@components/dataEntry/radio/RadioBox';
import DateInput from '@components/dataEntry/Input/DateInput/DateInput';
import AutoTextarea from '@components/dataEntry/textarea/AutoTextarea';
import { cardDefaultStyle } from '@styles/common/Card';
import { BettingOption, IBettingItem } from '@typings/betting/betting.interface';
import { createOrUpdateIBettingItem, deleteBettingItem } from '@store/firebase/betting/bettingStore';
import Label from '@components/dataEntry/Input/Label';
import { LabelDescription } from '@styles/common/SettingPage';
import UserAvatar from '@components/dataDisplay/Avatar/UserAvatar';

const FormSec = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;

  ${cardDefaultStyle}
`;

const OptionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

type Props = {
  editing: IBettingItem;
  setEditing: (t: IBettingItem) => void;
  announceDate?: Date | undefined;
  setAnnounceDate: (d: Date | undefined) => void;
  onSaveAll?: (payload: IBettingItem, announceTime: string) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  hideDelete?: boolean;
  title?: string;
  style?: React.CSSProperties;
  saveText?: string;
  customCollectionPath?: string;
  customBettingOpttion?: boolean;
};

function BettingSettingForm({
  editing,
  setEditing,
  announceDate,
  setAnnounceDate,
  onSaveAll,
  onDelete,
  hideDelete,
  title,
  style,
  saveText,
  customCollectionPath,
  customBettingOpttion = false,
}: Props) {
  const [announceTime, setAnnounceTime] = useState<string>('');

  useEffect(() => {
    if (announceDate) {
      const h = String(announceDate.getHours()).padStart(2, '0');
      const m = String(announceDate.getMinutes()).padStart(2, '0');
      setAnnounceTime(`${h}:${m}`);
    } else {
      setAnnounceTime('');
    }
  }, [announceDate]);

  const handleAddOption = useCallback(() => {
    const keyBase = (editing.title || 'opt')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .slice(0, 12);
    const seq = (editing.options?.length || 0) + 1;
    const newOpt: BettingOption = { key: `${keyBase}-${seq}`, label: '' };
    setEditing({ ...editing, options: [...(editing.options || []), newOpt] });
  }, [editing, setEditing]);

  const handleChangeOption = useCallback(
    (idx: number, label: string) => {
      const next = [...(editing.options || [])];
      next[idx] = {
        ...(next[idx] || ({} as BettingOption)),
        label,
      } as BettingOption;
      setEditing({ ...editing, options: next });
    },
    [editing, setEditing],
  );

  const handleRemoveOption = useCallback(
    (idx: number) => {
      setEditing({
        ...editing,
        options: (editing.options || []).filter((_, i) => i !== idx),
      });
    },
    [editing, setEditing],
  );

  const handleSave = useCallback(async () => {
    if (!editing.title || (editing.options || []).length < 2) return;

    let finalAnnounceAt: number = Number(editing.announceAt || 0);
    if (announceDate) {
      const d = new Date(announceDate);
      const [hh, mm] = (announceTime || '00:00').split(':');
      d.setHours(Number(hh) || 0, Number(mm) || 0, 0, 0);
      finalAnnounceAt = d.getTime();
    }

    const sanitizedOptions = (editing.options || []).map((opt, i) => ({
      key: (opt && typeof opt.key === 'string' ? opt.key : '') || `opt-${i + 1}`,
      label: (opt && typeof opt.label === 'string' ? opt.label : '') || '',
    }));

    const payload: IBettingItem = {
      ...(editing.id ? { id: String(editing.id) } : {}),
      title: String(editing.title || ''),
      options: sanitizedOptions,
      correctKey: String(editing.correctKey || ''),
      announceAt: Number(finalAnnounceAt || 0),
      createdAt: Number(editing.createdAt || Date.now()),
      updatedAt: Number(Date.now()),
      bettingHint: String(editing.bettingHint || ''),
      status: (editing.status as any) || 'pending',
      order: 0,
    } as IBettingItem;

    if (onSaveAll) {
      await onSaveAll(payload, announceTime);
      return;
    }

    const id = await createOrUpdateIBettingItem(payload, customCollectionPath);
    setEditing({ ...editing, ...payload, id });
  }, [editing, announceDate, announceTime, setEditing, onSaveAll]);

  const handleMoveToMaster = useCallback(async () => {
    // 현재 편집 중 항목을 메인 베팅 컬렉션(CollectionPath.BETTING)에 신규로 추가
    if (!editing) return;
    const sanitizedOptions = (editing.options || []).map((opt, i) => ({
      key: (opt && typeof opt.key === 'string' ? opt.key : '') || `opt-${i + 1}`,
      label: (opt && typeof opt.label === 'string' ? opt.label : '') || '',
    }));

    const payload: IBettingItem = {
      title: String(editing.title || ''),
      options: sanitizedOptions,
      correctKey: String(editing.correctKey || ''),
      announceAt: Number(editing.announceAt || 0),
      createdAt: Number(Date.now()),
      updatedAt: Number(Date.now()),
      bettingHint: String(editing.bettingHint || ''),
      status: (editing.status as any) || 'pending',
      order: Number(editing.order || 0),
    } as IBettingItem;

    const newId = await createOrUpdateIBettingItem(payload);
    alert('마스터 베팅 컬렉션으로 이동(복사)되었습니다.');
  }, [editing]);

  const handleDelete = useCallback(async () => {
    if (!editing?.id) return;
    if (onDelete) {
      await onDelete();
      return;
    }
    const ok = window.confirm('이 베팅 항목을 삭제하시겠습니까? 삭제 후 되돌릴 수 없습니다.');
    if (!ok) return;
    try {
      await deleteBettingItem(String(editing.id), customCollectionPath);
      alert('베팅 항목이 삭제되었습니다.');
      setEditing({ id: '', title: '', options: [], correctKey: '', announceAt: 0, bettingHint: '' } as IBettingItem);
    } catch (e) {
      console.error(e);
      alert('삭제 중 오류가 발생했습니다.');
    }
  }, [editing, setEditing, onDelete, customCollectionPath]);

  return (
    <FormSec style={style}>
      <h6>{title || '베팅 설정'}</h6>
      <TextInput
        name="title"
        placeholder="주제 제목"
        value={editing.title || ''}
        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
      />
      {/* 만든 이  - 데모 베팅일때만 표시*/}
      {customBettingOpttion && (
        <div>
          <Label>만든 이</Label>
          <UserAvatar uid={editing.authorUid || ''} avatarSize="24px" />
        </div>
      )}

      <div>
        <Label style={{ padding: 0 }}>현재 상태</Label>
        <LabelDescription>현재 상태 [진행중] 일 때만 베팅 목록에 표시됩니다.</LabelDescription>
        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          <RadioBox
            id="betting-status-pending"
            name="betting-status"
            value="pending"
            checked={(editing.status || 'pending') === 'pending'}
            onChange={() => setEditing({ ...editing, status: 'pending' })}
            shape="box"
          >
            대기중
          </RadioBox>
          <RadioBox
            id="betting-status-active"
            name="betting-status"
            value="active"
            checked={editing.status === 'active'}
            onChange={() => setEditing({ ...editing, status: 'active' })}
            shape="box"
          >
            진행중
          </RadioBox>
          <RadioBox
            id="betting-status-hidden"
            name="betting-status"
            value="hidden"
            checked={editing.status === 'hidden'}
            onChange={() => setEditing({ ...editing, status: 'hidden' })}
            shape="box"
          >
            숨김
          </RadioBox>
        </div>
      </div>

      <AutoTextarea
        label="베팅 힌트"
        name="bettingHint"
        placeholder="참여자에게 보여줄 힌트나 설명을 입력하세요."
        value={editing.bettingHint || ''}
        onChange={(e) => setEditing({ ...editing, bettingHint: e.target.value })}
        autoRows
        minRows={2}
        maxRows={8}
      />

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Label>선택지</Label>
          <div style={{ display: 'flex', gap: 8 }}>
            {!hideDelete && (
              <Button size="sm" shape="stroke" onClick={handleDelete}>
                삭제
              </Button>
            )}
            <Button size="sm" onClick={handleAddOption}>
              선택지 추가
            </Button>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {(editing.options || []).map((opt, idx) => (
            <OptionRow key={(opt && opt.key) || idx}>
              <TextInput
                name={`opt-${idx}`}
                placeholder={`선택지 ${idx + 1}`}
                value={(opt && opt.label) || ''}
                onChange={(e) => handleChangeOption(idx, e.target.value)}
              />
              <Button
                size="sm"
                shape="stroke"
                onClick={() => handleRemoveOption(idx)}
              >
                삭제
              </Button>
            </OptionRow>
          ))}
        </div>
      </div>

      <div>
        <Label>정답지</Label>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {(editing.options || []).map((opt, i) => (
            <RadioBox
              key={(opt && opt.key) || i}
              value={(opt && opt.key) || ''}
              id={`betting-correct-${i}`}
              name="betting-correct"
              checked={(editing.correctKey || '') === ((opt && opt.key) || '')}
              onChange={() =>
                setEditing({ ...editing, correctKey: (opt && opt.key) || '' })
              }
              shape="box"
            >
              <span>{(opt && opt.label) || `선택지 ${i + 1}`}</span>
            </RadioBox>
          ))}
        </div>
      </div>

      <div>
        <Label>발표일/시간</Label>
        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
          <DateInput
            name="announceAt"
            date={announceDate}
            setDate={setAnnounceDate}
          />
          <TextInput
            name="announceTime"
            placeholder="HH:mm"
            value={announceTime}
            onChange={(e) =>
              setAnnounceTime(
                e.target.value.replace(/[^0-9:]/g, '').slice(0, 5),
              )
            }
            style={{ width: 120 }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {!hideDelete && (
          <Button onClick={handleDelete} color="warning" block>
            삭제
          </Button>
        )}
        {customBettingOpttion && (
          <Button onClick={handleMoveToMaster} block shape="stroke">
            마스터 베팅 설정으로 이동
          </Button>
        )}
        <Button onClick={handleSave} block>
          {saveText || '저장'}
        </Button>
      </div>
    </FormSec>
  );
}

export default BettingSettingForm;


